import React from 'react';
import { motion } from 'framer-motion';
import { Bed, Users, Square, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const ResortCard = ({ resort, villa, delay = 0, onCompare, isSelected }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: delay * 0.1, duration: 0.6 }}
      className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-[0_20px_50px_rgb(0,0,0,0.1)] transition-all duration-500 border border-border/40 flex flex-col md:flex-row h-auto md:h-80"
    >
      {/* Compare Checkbox */}
      <div className="absolute top-4 left-4 z-20">
         <label className="flex items-center gap-2 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full cursor-pointer shadow-sm border border-border/50 hover:bg-white transition-colors">
            <input 
              type="checkbox" 
              checked={isSelected || false}
              onChange={(e) => onCompare(villa, e.target.checked)}
              className="rounded-full border-primary text-primary focus:ring-0 w-4 h-4"
            />
            <span className="text-xs font-bold uppercase tracking-wider text-primary">Compare</span>
         </label>
      </div>

      {/* Image Section */}
      <div className="w-full md:w-2/5 relative overflow-hidden h-64 md:h-full">
        <img 
          src={villa.images[0] || resort.images[0]} 
          alt={villa.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase text-primary shadow-sm">
          {resort.resort_name}
        </div>
        <div className="absolute bottom-4 left-4 flex gap-2">
           {villa.has_pool && (
             <span className="bg-black/40 backdrop-blur-md text-white px-2 py-1 rounded text-[10px] font-medium border border-white/20">Private Pool</span>
           )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-6 md:p-8 flex flex-col justify-between relative">
        <div className="absolute top-6 right-6 flex items-center gap-1 text-accent font-heading font-bold">
           <Star size={14} fill="currentColor" />
           <span>{resort.resort_stars}</span>
        </div>

        <div>
          <h3 className="text-2xl font-heading font-medium text-primary mb-2 group-hover:text-accent transition-colors">
            {villa.name}
          </h3>
          <p className="text-muted-foreground line-clamp-2 text-sm leading-relaxed mb-6 font-light">
            {villa.description}
          </p>
          
          <div className="grid grid-cols-2 gap-4 text-sm text-primary/80">
            <div className="flex items-center gap-2">
              <Users size={16} className="text-muted-foreground" />
              <span>Up to {villa.occupancy} Guests</span>
            </div>
            <div className="flex items-center gap-2">
              <Bed size={16} className="text-muted-foreground" />
              <span>{villa.bedrooms} Bedroom{villa.bedrooms > 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              <Square size={16} className="text-muted-foreground" />
              <span>{villa.size_sqm} m²</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-border/40">
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground uppercase tracking-widest">From</span>
            <span className="text-2xl font-heading font-bold text-primary">${villa.price_per_night}<span className="text-sm font-sans font-normal text-muted-foreground">/night</span></span>
          </div>
          <Link to={`/villa/${villa.id}`}>
            <button 
                className="bg-primary text-white px-6 py-3 rounded-full text-xs font-bold tracking-widest uppercase hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                data-testid={`view-villa-${villa.id}`}
            >
                View Details
            </button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

export default ResortCard;
